import { IWorkspaceSearcher } from '../components/WorkspaceSelectorViewHeading'
import { action, computed, makeObservable, observable, reaction } from 'mobx'
import { GetCurrentUserWorkspaceMemberships } from '../iam/WorkspaceMembershipStore'
import Workspace from '../iam/models/Workspace'
import { SingleSelectFilter } from '../infra/FilterControls/SingleSelect/SingleSelectFilter'
import { GetCurrentMember } from '../iam/SessionStore'
import { Subscription } from 'rxjs'
import { SearchTextViewModel } from '../infra/SearchTextViewModel'
import WorkspaceSelectorState from '../iam/views/AuthenticationContainer/WorkspaceSelector/WorkspaceSelectorState'
import {
  LastWorkspaceStorage,
  ALL_WORKSPACES_SELECTION,
} from '../infra/LastWorkspace'

export class WorkspaceSelectorViewModel implements IWorkspaceSearcher {
  private searchSubscription = new Subscription()

  private textSearcher = new SearchTextViewModel(500)

  private globalWorkspaceReactionDisposer: (() => void) | null = null

  @observable
  editing = false

  constructor(
    private getCurrentMember: GetCurrentMember,
    private getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private workspaceFilter: () => SingleSelectFilter<string, Workspace>,
    private clearSelectedUsers: () => void,
    private clearWorkspaceScopedFilters: () => void,
    private workspaceSelectorState: WorkspaceSelectorState,
    private lastWorkspaceStorage: LastWorkspaceStorage,
    private getUserId: () => string,
  ) {
    makeObservable(this)
  }

  activate() {
    this.searchSubscription = this.textSearcher.debouncedValue$.subscribe()

    // React to global workspace selector changes
    this.globalWorkspaceReactionDisposer = reaction(
      () => this.workspaceSelectorState.workspace?.id,
      (workspaceId) => {
        if (workspaceId) {
          const workspace = this.workspaces.find((w) => w.id === workspaceId)
          if (workspace) {
            this.selectWorkspace(workspace)
          }
        }
      },
      { fireImmediately: false },
    )
  }

  setDefaultWorkspace() {
    // Check localStorage first for a previously selected workspace
    const lastWorkspaceId = this.lastWorkspaceStorage.load(this.getUserId())
    if (lastWorkspaceId) {
      // Check if "All workspaces" was explicitly selected
      if (lastWorkspaceId === ALL_WORKSPACES_SELECTION && this.canManageAll) {
        return this.selectWorkspace(Workspace.none)
      }

      const lastWorkspace = this.workspaces.find(
        (w) => w.id === lastWorkspaceId,
      )
      if (lastWorkspace) {
        return this.selectWorkspace(lastWorkspace)
      }
    }

    // Fall back to existing default logic
    if (
      this.canManageAll &&
      this.workspaceSelectorState.isDefaultToAllWorkspaces
    ) {
      return this.selectWorkspace(Workspace.none)
    }

    const memberWorkspace = this.getCurrentMember().workspace

    return this.selectWorkspace(
      this.workspaces.some((workspace) => workspace.id === memberWorkspace.id)
        ? memberWorkspace
        : this.workspaces[0],
    )
  }

  deactivate() {
    this.searchSubscription.unsubscribe()
    this.globalWorkspaceReactionDisposer?.()
    this.globalWorkspaceReactionDisposer = null
  }

  @computed
  get searchText() {
    return this.textSearcher.value
  }

  @computed
  get selectedWorkspace() {
    return this.workspaceFilter().value
  }

  @computed
  get filteredWorkspaces() {
    const searchText = this.searchText.toLowerCase()

    if (searchText) {
      return this.workspaces.filter((ws) =>
        ws.name.toLocaleLowerCase().includes(searchText),
      )
    }

    return this.workspaces
  }

  @computed
  get canManageAll() {
    return this.getCurrentUserWorkspaceMemberships().some(({ member }) =>
      member.hasPermission('ADMIN_GLOBAL_USER_LIST'),
    )
  }

  @computed
  get workspaces() {
    return this.getCurrentUserWorkspaceMemberships()
      .filter(({ member }) => member.hasPermission('ADMIN_USER_LIST'))
      .map((member) => member.workspace)
  }

  @computed
  get canInviteUsers() {
    return this.getCurrentUserWorkspaceMemberships().some(({ member }) => {
      const hasAdminInvite = member.hasAllPermissions(
        'ADMIN_ROLE_LIST',
        'ADMIN_TEAMS_VIEW',
        'ADMIN_USER_CREATE',
      )
      const externalCustomersConfig =
        member.workspace.config?.external_customers_config
      const hasCustomerInvite =
        member.hasPermission('CUSTOMER_CREATE') &&
        externalCustomersConfig?.enabled &&
        externalCustomersConfig?.allow_requesting_magic_links

      return hasAdminInvite || hasCustomerInvite
    })
  }

  @action.bound
  selectWorkspace(workspace: Workspace) {
    this.workspaceFilter().change(workspace)
    this.clearSelectedUsers()
    this.clearWorkspaceScopedFilters()

    if (workspace !== Workspace.none && workspace.id) {
      this.lastWorkspaceStorage.save(workspace.id, this.getUserId())
    } else {
      // Save special value to indicate "All workspaces" was explicitly selected
      this.lastWorkspaceStorage.save(ALL_WORKSPACES_SELECTION, this.getUserId())
    }
  }

  @action.bound
  setSearchText(searchText: string) {
    this.textSearcher.change(searchText)
  }

  @action.bound
  startEditing() {
    this.editing = true
  }

  @action.bound
  stopEditing() {
    this.editing = false
  }
}
